import Image from 'next/image';
import { SmartLink } from '@/components/utils/SmartLink';
import { ClickTracker } from '@/components/utils/ClickTracker';

export interface PromotionData {
  header?: string;
  title: string;
  description: string;
  image?: {
    node?: {
      id?: string;
      sourceUrl?: string;
      altText?: string;
      mediaDetails?: {
        width?: number;
        height?: number;
      };
    };
  };
  cta?: {
    title?: string;
    target?: string;
    url?: string;
  };
}

interface PromotionServerProps {
  className?: string;
  promotionData: PromotionData;
}

export default function PromotionServer({
  className = '',
  promotionData,
}: PromotionServerProps) {
  if (!promotionData) return null;

  const imageUrl = promotionData.image?.node?.sourceUrl;
  const imageAlt = promotionData.image?.node?.altText || promotionData.title;
  const cta = promotionData.cta;

  return (
    <article className={`bg-white rounded-xl border border-gray-100 shadow-md overflow-hidden hover:shadow-lg transition-shadow duration-200 ${className}`}>
      {promotionData.header && (
        <div className="bg-[#f16232] text-white text-center py-2 text-xs font-semibold">{promotionData.header}</div>
      )}
      <div className="p-3">
        {/* Row 1: Image + Title */}
        <div className="flex items-center gap-2">
          {imageUrl && (
            <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-gray-50 flex items-center justify-center overflow-hidden border border-gray-100">
              <Image
                src={imageUrl}
                title={promotionData.title}
                alt={imageAlt}
                width={40}
                height={40}
                className="rounded-lg object-contain p-0.5"
                priority={false}
                loading="lazy"
              />
            </div>
          )}
          <h4 className="text-xs font-semibold text-gray-900 leading-snug flex-1 min-w-0">
            {promotionData.title}
          </h4>
        </div>

        {/* Row 2: Full-width button */}
        {cta?.url && cta?.title && (
          <ClickTracker
            eventName="Promotion Click"
            data={{ title: cta.title, url: cta.url }}
          >
            <SmartLink
              href={cta.url}
              target={cta.target || '_blank'}
              className="mt-2 flex items-center justify-center gap-2 w-full py-2 px-3 bg-[#f16232] hover:bg-[#d9542a] text-white hover:text-white text-xs font-semibold rounded-lg transition-colors duration-200 shadow-sm hover:shadow-md focus:outline-none focus:ring-2 focus:ring-[#f16232] focus:ring-offset-2"
              aria-label={`${cta.title} - ${promotionData.title}`}
              title={`${cta.title} - ${promotionData.title}`}
            >
              {cta.title}
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
              </svg>
            </SmartLink>
          </ClickTracker>
        )}

        {/* Row 3: Disclaimer under button */}
        {promotionData.description && (
          <p className="m-0 mt-1.5 text-[10px] text-gray-500 leading-relaxed">
            {promotionData.description}
          </p>
        )}
      </div>
    </article>
  );
}
